// The message classes and the nine-byte header, ported from internal/wire/wire.go.
//
// Every message starts with the same header — a one-byte class tag and a
// little-endian u64 tick — because gap detection is arithmetic on that number and
// must not depend on which class the frame happens to be.
//
// The tag numbering is NOT tidy and must not be tidied. ClassTick is 7 because it
// was added after the first six; the four client-facing classes are 8..11 because
// they were defined in internal/hub first and moving them changed no byte on the
// wire. A format that shuffles its own tags to look neat is a format two
// implementations disagree about.

import { errClass, errTickRange, errTruncated } from './errors';
import type { TickNo } from './core';

export const ClassInvalid = 0;
export const ClassKeyframe = 1;
export const ClassDelta = 2;
export const ClassSelf = 3;
export const ClassEphemeral = 4;
export const ClassAggregate = 5;
export const ClassSubscribe = 6;
export const ClassTick = 7;
export const ClassHello = 8;
export const ClassWelcome = 9;
export const ClassClick = 10;
export const ClassReject = 11;

export type Class =
  | typeof ClassInvalid
  | typeof ClassKeyframe
  | typeof ClassDelta
  | typeof ClassSelf
  | typeof ClassEphemeral
  | typeof ClassAggregate
  | typeof ClassSubscribe
  | typeof ClassTick
  | typeof ClassHello
  | typeof ClassWelcome
  | typeof ClassClick
  | typeof ClassReject;

const CLASS_NAMES: Record<number, string> = {
  [ClassKeyframe]: 'KEYFRAME',
  [ClassDelta]: 'DELTA',
  [ClassSelf]: 'SELF',
  [ClassEphemeral]: 'EPHEMERAL',
  [ClassAggregate]: 'AGGREGATE',
  [ClassSubscribe]: 'SUBSCRIBE',
  [ClassTick]: 'TICK',
  [ClassHello]: 'HELLO',
  [ClassWelcome]: 'WELCOME',
  [ClassClick]: 'CLICK',
  [ClassReject]: 'REJECT',
};

export function className(c: number): string {
  return CLASS_NAMES[c] ?? 'INVALID';
}

/**
 * Droppable reports whether the backpressure ladder of SPEC §3 may shed this
 * class. Only EPHEMERAL may be: everything else is state, and dropping state
 * silently desynchronises a follower with no way to ask for a resend.
 */
export function droppable(c: number): boolean {
  return c === ClassEphemeral;
}

/** One class byte plus a u64 tick. */
export const HeaderSize = 1 + 8;

export interface Header {
  class: Class;
  tick: TickNo;
}

/**
 * Reads the header without consuming the body, for a demultiplexer that
 * dispatches on class before it knows which decoder to call.
 */
export function parseHeader(b: Uint8Array): Header {
  if (b.byteLength < HeaderSize) throw errTruncated();
  const c = b[0] as Class;
  if (c === ClassInvalid || c > ClassReject) throw errClass();
  const view = new DataView(b.buffer, b.byteOffset, b.byteLength);
  const tick = view.getBigUint64(1, true);
  if (tick > BigInt(Number.MAX_SAFE_INTEGER)) throw errTickRange();
  return { class: c, tick: Number(tick) };
}

/** The class byte alone, matching wire.PeekClass. */
export function peekClass(b: Uint8Array): Class {
  if (b.byteLength < HeaderSize) throw errTruncated();
  const c = b[0] as Class;
  if (c === ClassInvalid || c > ClassReject) throw errClass();
  return c;
}
